import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '@environment';
import { consoleLog } from '@functions/functions';
import { AuthService, LoadingService, MessagesService } from '@services/services';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Token } from '../classes/token';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<number | null>(null);

export const tokenInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const messagesService = inject(MessagesService);
  const loadingService = inject(LoadingService);

  request = cloneRequest(request, authService.token$.getValue());

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      loadingService.hide();
      if (!(error instanceof ErrorEvent)) {
        switch (error.status) {
          case 0:
            if (
              !error.url?.includes(`/api/account/refreshSession`) &&
              !error.url?.includes(`/api/errors`)
            ) {
              messagesService.showAlert('No se ha podido conectar con el servidor.');
            }
            break;

          case 400:
            if (error.error?.error === 'invalid_grant') {
              if (error.error.error_description === 'invalid_username_or_password') {
                messagesService.showAlert('USUARIO O CONTRASEÑA INCORRECTAS');
              } else {
                authService.redirectToLogin();
              }
            }
            break;

          case 401:
            if (request.url.includes(`/api/account/refreshSession`)) {
              messagesService.showAlert('Sesión expirada.');
              authService.redirectToLogin();
              return throwError(() => error);
            }

            return handle401Error(request, next, authService, messagesService);

          case 500:
            showErrors(error, messagesService, authService);
            break;
        }
      }
      return throwError(() => error);
    }),
  );
};

function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  messagesService: MessagesService,
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);
    return authService.refreshToken().pipe(
      switchMap((tokenResponse) => {
        isRefreshing = false;
        authService.setToken(tokenResponse);
        refreshTokenSubject.next(1);
        const reqWithToken = cloneRequest(request, authService.token$.getValue());
        return next(reqWithToken);
      }),

      catchError((error: HttpErrorResponse) => {
        isRefreshing = false;
        if (error.status === 401) {
          messagesService.showAlert('Sesión expirada.');
          authService.redirectToLogin();
          return throwError(() => error);
        }
        return throwError(() => error);
      }),
    );
  }

  return refreshTokenSubject.pipe(
    filter((token) => token != null),
    take(1),
    switchMap(() => {
      const reqWithToken = cloneRequest(request, authService.token$.getValue());
      return next(reqWithToken);
    }),
  );
}

function cloneRequest(request: HttpRequest<unknown>, token: Token | null): HttpRequest<unknown> {
  const headers: Record<string, string> = {
    'accept-Language': environment.languageId,
    'app-Version-Number': '1.1.34',
  };

  if (token?.accessToken && token?.type) {
    headers['Authorization'] = `${token.type} ${token.accessToken}`;
  }

  return request.clone({
    setHeaders: headers,
  });
}

function showErrors(
  error: HttpErrorResponse,
  messagesService: MessagesService,
  authService: AuthService,
): void {
  consoleLog(JSON.stringify(error));
  if (!isJson(error.error)) {
    if (error.error.toString().includes('Sesión no encontrada')) {
      messagesService.showAlert('Sesión expirada.');
      authService.redirectToLogin();
      return;
    }
    messagesService.showAlert(error.error.toString());
    return;
  }

  const objError = JSON.parse(error.error);

  if (objError.errors && objError.errors.length !== 0) {
    Object.values(objError.errors).forEach((value: unknown) =>
      (value as string[]).forEach((msg: unknown) =>
        messagesService.showAlert(msg as string, 'Error de validación'),
      ),
    );
  }
}

function isJson(str: string): boolean {
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
}
